import { Fragment, Suspense } from 'react';
import { Navigate, Outlet, RouterProvider, createBrowserRouter } from 'react-router-dom';

import { AnnouncementsBanner } from '@spacefill/external-console/src/layout/Annoucements/AnnouncementsBanner';
import { AutoRefresh } from '@spacefill/shared/src/utils/AutoRefresh';
import { useFeatureTogglingContext } from '@spacefill/shared/src/utils/FeatureToggling';
import { getEnvironmentVariable } from '@spacefill/shared/src/utils/GetEnvVar';
import { createRoutesFromObject } from '@spacefill/shared/src/utils/RouterUtils';
import { useSidebarVersionContext } from '@spacefill/shared/src/utils/contexts/SidebarVersionContext';
import { useUserContext } from '@spacefill/shared/src/utils/contexts/UserContext';
import { SpacefillDebugActions } from '@spacefill/uikit/src/SpacefillDebugActions';
import { AppLayout } from '@spacefill/uikit/src/components/AppLayout';
import { Loader } from '@spacefill/uikit/src/components/Loader';

import { AppSidebar } from '../layout/AppSidebar';
import { AppSidebar as AppSidebarLegacy } from '../layout/AppSidebarLegacy';
import { SpacefillTopbar } from '../layout/Topbar';
import { ErrorComponent } from './ErrorComponent';
import { getLoggedUserRoutes } from './LoggedRouter';
import { getUnloggedUserRoutes } from './UnloggedRouter';

const LogoutPage = async () =>
    import('@spacefill/shared/src/pages/Logout').then((module) => ({
        Component: module.Logout,
    }));

const EnvView = async () =>
    import('@spacefill/shared/src/pages/Env').then((module) => ({
        Component: module.EnvView,
    }));

const RouterContext = () => {
    const { user } = useUserContext();
    const { features } = useFeatureTogglingContext();
    const { sidebarVersion } = useSidebarVersionContext();

    let router;
    const loader = (
        <div className='h-screen w-screen'>
            <Loader />
        </div>
    );

    if (user?.logged) {
        router = createBrowserRouter(
            [
                // Version
                {
                    path: '/environment/',
                    lazy: EnvView,
                    errorElement: <ErrorComponent />,
                },
                {
                    path: '/environment',
                    element: <Navigate to='/environment/' />,
                    errorElement: <ErrorComponent />,
                },
                // Logout
                {
                    path: '/logout/',
                    lazy: LogoutPage,
                },

                // UI Routes
                {
                    element:
                        !user?.customer?.isLoaded || !user?.logisticProvider?.isLoaded ? (
                            <Suspense fallback={loader}>{loader}</Suspense>
                        ) : (
                            <Suspense fallback={loader}>
                                {getEnvironmentVariable('mode') !== 'development' && (
                                    <AutoRefresh localStorageKey='externalConsoleVersion' />
                                )}
                                <Outlet />
                            </Suspense>
                        ),
                    children: createRoutesFromObject(
                        getLoggedUserRoutes(user, features),
                        <AppLayout
                            topbar={<SpacefillTopbar />}
                            leftNav={sidebarVersion === 'legacy' ? <AppSidebarLegacy /> : <AppSidebar />}
                            headerContent={<AnnouncementsBanner />}
                        >
                            <Outlet />
                        </AppLayout>
                    ),
                    errorElement: <ErrorComponent />,
                },
            ],
            {
                basename: import.meta.env?.VITE_EXTERNAL_CONSOLE_ROUTER_BASE_NAME,
            }
        );
    } else {
        router = createBrowserRouter(
            [
                {
                    element: (
                        <Suspense fallback={<Fragment />}>
                            <Outlet />
                            <div className='fixed bottom-2 left-2'>
                                {getEnvironmentVariable('featureTogglingEnabled') && <SpacefillDebugActions />}
                            </div>
                        </Suspense>
                    ),
                    children: createRoutesFromObject(getUnloggedUserRoutes()),
                    errorElement: <ErrorComponent />,
                },
            ],
            {
                basename: import.meta.env?.VITE_EXTERNAL_CONSOLE_ROUTER_BASE_NAME,
            }
        );
    }

    return <RouterProvider router={router} />;
};

export { RouterContext };
